// src/pages/DraftWars/CaptainRoom.jsx
// Captain's private bidding dashboard — accessed via tokenized URL.

import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { onSnapshot } from "firebase/firestore";
import { FaGavel, FaTrophy, FaClock, FaCoins } from "react-icons/fa";
import { refs, placeBid, calcMaxBid } from "./draftWarsEngine";
import { CAPTAIN_IMAGES, PLAYER_IMAGES, TIER_META, QUICK_BID_AMOUNTS } from "./draftWarsData";
import { useCountdown, useServerTimeSync } from "./draftWarsHooks";

const fmt = (n) => "$" + (n ?? 0).toLocaleString();

function CaptainRoom() {
  useServerTimeSync();
  const { token } = useParams();
  const [session, setSession] = useState(null);
  const [captains, setCaptains] = useState([]);
  const [players, setPlayers] = useState([]);
  const [bidding, setBidding] = useState(false);
  const [bidErr, setBidErr] = useState("");

  // Session
  useEffect(() => {
    return onSnapshot(refs.session, (snap) => setSession(snap.exists() ? snap.data() : null));
  }, []);

  // Captains (needed to find self by token)
  useEffect(() => {
    return onSnapshot(refs.captains, (snap) => {
      setCaptains(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  // Players (for roster display)
  useEffect(() => {
    return onSnapshot(refs.players, (snap) => {
      setPlayers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, []);

  const me = useMemo(() => captains.find((c) => c.token === token), [captains, token]);
  const auction = session?.currentAuction || null;
  const secondsLeft = useCountdown(auction?.timerEndAt);
  const settings = session?.settings || {};
  const maxBid = me && settings ? calcMaxBid(me, settings) : 0;

  // Roster players resolved
  const myRoster = useMemo(() => {
    if (!me) return [];
    return (me.roster || []).map((pid) => players.find((p) => p.id === pid)).filter(Boolean);
  }, [me, players]);

  const handleBid = async (amount) => {
    setBidding(true); setBidErr("");
    try {
      await placeBid(token, amount);
    } catch (e) {
      setBidErr(e.message);
      setTimeout(() => setBidErr(""), 3000);
    } finally {
      setBidding(false);
    }
  };

  // ── Loading / invalid states ────────────────────────────────────────
  if (session === null && captains.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <p className="text-gray-500 animate-pulse">Connecting…</p>
      </div>
    );
  }
  if (!me) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-md text-center space-y-3">
          <FaGavel className="text-red-600 mx-auto" size={32} />
          <h1 className="text-2xl font-black uppercase text-white">Invalid Link</h1>
          <p className="text-gray-500 text-sm">This captain link is not recognized. Please contact the admin to get a new one.</p>
        </div>
      </div>
    );
  }

  const statusLabel = session?.status === "setup" ? "Draft starts soon" :
    session?.status === "complete" ? "Draft complete" :
    auction ? (secondsLeft === 0 ? "Awaiting confirmation…" : "Bidding live") :
    "Waiting for next player";

  const img = CAPTAIN_IMAGES[me.name];
  const slotsLeft = (settings.rosterSize || 3) - (me.roster?.length || 0);

  return (
    <div className="min-h-screen bg-black text-white pb-24">
      {/* Captain header */}
      <header className="sticky top-0 z-40 bg-gray-950/95 backdrop-blur-sm border-b border-gray-800 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-900 border-2 flex-shrink-0" style={{ borderColor: me.color + "80" }}>
            {img ? <img src={img} alt={me.name} className="w-full h-full object-cover" decoding="async" /> : <div className="w-full h-full flex items-center justify-center font-black">{me.name.charAt(0)}</div>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-wider text-gray-500">Captain</p>
            <h1 className="text-lg font-black uppercase text-white truncate">{me.name}</h1>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-gray-500 flex items-center gap-1 justify-end"><FaCoins className="text-green-400" size={10} /> Budget</p>
            <p className="text-lg font-black text-green-400 tabular-nums">{fmt(me.budgetRemaining)}</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Status bar */}
        <div className="text-center">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest bg-gray-900 border border-gray-800 text-gray-400">
            <span className={`w-1.5 h-1.5 rounded-full ${auction ? "bg-green-500 animate-pulse" : "bg-gray-600"}`} />
            {statusLabel}
          </span>
        </div>

        {/* Auction card */}
        {auction ? (
          <div key={auction.playerId} className={`relative overflow-hidden rounded-2xl border transition-all animate-[fadeIn_0.4s_ease-out] ${
            secondsLeft !== null && secondsLeft <= 2 ? "border-red-500/60 shadow-2xl shadow-red-500/20" : "border-gray-800"
          } bg-gradient-to-br from-gray-900 to-gray-950`}>
            <div className="p-6 space-y-5">
              {/* Player info */}
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0 shadow-lg border-2"
                  style={{
                    borderColor: (TIER_META[auction.tier]?.color || "#6B7280") + "99",
                    backgroundColor: TIER_META[auction.tier]?.color || "#374151",
                  }}>
                  {PLAYER_IMAGES[auction.playerName] ? (
                    <img src={PLAYER_IMAGES[auction.playerName]} alt={auction.playerName}
                      className="w-full h-full object-cover object-top" decoding="async" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center font-black text-3xl bg-gradient-to-br ${TIER_META[auction.tier]?.accent || ""} text-white`}>
                      {auction.tier}
                    </div>
                  )}
                  <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded text-white text-[10px] font-black shadow"
                    style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                    {auction.tier}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-xs uppercase tracking-wider text-gray-500">On the block</span>
                    <span className="px-1.5 py-0.5 rounded text-white text-[9px] font-black tracking-widest uppercase"
                      style={{ backgroundColor: TIER_META[auction.tier]?.color }}>
                      {TIER_META[auction.tier]?.label || (auction.tier + "-Tier")}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-white truncate">{auction.playerName}</h2>
                  <p className="text-sm text-gray-500">Base: {fmt(auction.basePrice)}</p>
                </div>
              </div>

              {/* Timer + high bid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-black/40 border border-gray-800 rounded-xl p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center justify-center gap-1">
                    <FaClock size={10} /> Timer
                  </p>
                  <p className={`text-4xl font-black tabular-nums ${
                    secondsLeft === null ? "text-gray-600" :
                    secondsLeft <= 2 ? "text-red-500 animate-pulse" : "text-white"
                  }`}>
                    {secondsLeft === null ? "—" : secondsLeft + "s"}
                  </p>
                </div>
                <div className="bg-black/40 border border-gray-800 rounded-xl p-4 text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">High bid</p>
                  {auction.highBid ? (
                    <>
                      <p className="text-2xl font-black text-green-400 tabular-nums">{fmt(auction.highBid)}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">
                        {auction.highBidder === me.id ? <span className="text-green-400 font-bold">You're leading</span> : auction.highBidderName}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500 mt-2">No bids yet</p>
                  )}
                </div>
              </div>

              {/* Bidding controls */}
              <BidSection
                bidding={bidding}
                maxBid={maxBid}
                auction={auction}
                meId={me.id}
                onBid={handleBid}
                secondsLeft={secondsLeft}
                slotsLeft={slotsLeft}
              />

              {bidErr && (
                <p className="text-center text-red-400 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
                  {bidErr}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-gray-900/50 border border-dashed border-gray-800 rounded-2xl p-10 text-center space-y-2">
            <FaGavel className="text-gray-700 mx-auto" size={28} />
            <p className="text-gray-500">{statusLabel}</p>
          </div>
        )}

        {/* Roster */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-black uppercase tracking-widest text-gray-300 flex items-center gap-2">
              <FaTrophy size={12} className="text-red-500" /> Your Team
            </h3>
            <span className="text-xs text-gray-500">{myRoster.length}/{settings.rosterSize || 3} drafted</span>
          </div>
          <div className="space-y-2">
            {Array.from({ length: settings.rosterSize || 3 }).map((_, i) => {
              const p = myRoster[i];
              if (p) {
                return (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2 bg-gray-800/50 border border-gray-700 rounded-lg">
                    <div className="relative w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 border"
                      style={{
                        borderColor: (TIER_META[p.tier]?.color || "#6B7280") + "99",
                        backgroundColor: TIER_META[p.tier]?.color || "#374151",
                      }}>
                      {PLAYER_IMAGES[p.name] ? (
                        <img src={PLAYER_IMAGES[p.name]} alt={p.name}
                          className="w-full h-full object-cover object-top" loading="lazy" decoding="async" />
                      ) : (
                        <div className={`w-full h-full flex items-center justify-center font-black text-sm bg-gradient-to-br ${TIER_META[p.tier]?.accent || ""} text-white`}>
                          {p.tier}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-black text-[10px]" style={{ color: TIER_META[p.tier]?.color }}>{p.tier}</span>
                        <p className="text-white font-semibold truncate">{p.name}</p>
                      </div>
                      <p className="text-xs text-gray-500 tabular-nums">{fmt(p.soldFor)}</p>
                    </div>
                  </div>
                );
              }
              return (
                <div key={"slot" + i} className="flex items-center gap-3 px-3 py-2 bg-gray-900/50 border border-dashed border-gray-800 rounded-lg">
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex-shrink-0" />
                  <p className="text-gray-600 text-sm italic">Empty slot</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Bid Section ────────────────────────────────────────────────────────────
// Shows BUY button (before first bid) or 3 quick-bid buttons (after first bid).
// Also handles all the status states: leading, blocked, time's up, etc.
function BidSection({ bidding, maxBid, auction, meId, onBid, secondsLeft, slotsLeft }) {
  const statusBase = "w-full py-6 rounded-2xl font-black uppercase tracking-widest text-xl select-none";
  const statusClasses = {
    leading: "bg-green-700/20 border-2 border-green-500/50 text-green-400",
    blocked: "bg-gray-900 border-2 border-gray-800 text-gray-600",
    waiting: "bg-gray-900 border-2 border-gray-800 text-gray-500",
  };

  const StatusBlock = ({ label, sub, state }) => (
    <div className={`${statusBase} ${statusClasses[state]}`}>
      <div className="text-center">{label}</div>
      {sub && <div className="text-xs font-semibold normal-case tracking-normal text-inherit opacity-70 mt-1 text-center">{sub}</div>}
    </div>
  );

  // Status states first
  if (slotsLeft <= 0) {
    return <StatusBlock state="blocked" label="Roster Full" sub="You've drafted all 3 players" />;
  }
  if (!auction) {
    return <StatusBlock state="waiting" label="Waiting" />;
  }
  if (auction.phase === "nominated") {
    return <StatusBlock state="waiting" label="Get Ready" sub="Admin is about to open bidding…" />;
  }
  if (secondsLeft === 0 && auction.phase === "active") {
    return <StatusBlock state="waiting" label="Time's Up" sub="Winner auto-confirming…" />;
  }
  if (auction.highBidder === meId) {
    return <StatusBlock state="leading" label="You're Leading" sub="Waiting for counter-bid" />;
  }

  // ── FIRST BID: single BUY button at base price ──
  if (!auction.highBid) {
    const canBuy = auction.basePrice <= maxBid;
    return (
      <div className="space-y-2">
        <button onClick={() => onBid(auction.basePrice)} disabled={!canBuy || bidding}
          className={`w-full py-6 rounded-2xl font-black uppercase tracking-widest text-xl transition-all duration-150 select-none ${
            canBuy
              ? "bg-gradient-to-b from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 text-white shadow-lg shadow-red-500/30 active:scale-[0.98]"
              : "bg-gray-900 border-2 border-gray-800 text-gray-600 cursor-not-allowed"
          } disabled:opacity-90`}>
          <div>{bidding ? "Sending…" : canBuy ? `BUY at ${fmt(auction.basePrice)}` : "Exceeds your max bid"}</div>
          {canBuy && <div className="text-xs font-semibold normal-case tracking-normal opacity-70 mt-1">First bid — starts the 15s clock</div>}
        </button>
      </div>
    );
  }

  // ── SUBSEQUENT BIDS: 3 quick-bid buttons ──
  return (
    <div className="space-y-2">
      <p className="text-xs uppercase tracking-widest text-gray-500 text-center">Pick your raise</p>
      <div className="grid grid-cols-3 gap-2">
        {QUICK_BID_AMOUNTS.map((inc) => {
          const total = auction.highBid + inc;
          const canUse = total <= maxBid;
          return (
            <button key={inc} onClick={() => onBid(total)} disabled={!canUse || bidding}
              className={`py-5 rounded-xl font-black uppercase tracking-widest transition-all duration-150 select-none flex flex-col items-center justify-center gap-0.5 ${
                canUse
                  ? "bg-gradient-to-b from-red-500 to-red-700 hover:from-red-400 hover:to-red-600 text-white shadow-lg shadow-red-500/30 active:scale-[0.97]"
                  : "bg-gray-900 border-2 border-gray-800 text-gray-700 cursor-not-allowed opacity-60"
              }`}>
              <div className="text-base leading-none">+{fmt(inc)}</div>
              <div className="text-[10px] font-semibold normal-case tracking-normal opacity-80">{fmt(total)}</div>
            </button>
          );
        })}
      </div>
      {maxBid < auction.highBid + QUICK_BID_AMOUNTS[0] && (
        <p className="text-xs text-center text-gray-500 pt-1">
          Max bid reached · ceiling {fmt(maxBid)}
        </p>
      )}
    </div>
  );
}

export default CaptainRoom;
